/**
 * WordPress CMS Client
 * 
 * Provides a clean abstraction for fetching content from WordPress headless CMS.
 * All content is fetched from the WordPress REST API.
 */

const WORDPRESS_URL = process.env.NEXT_PUBLIC_WORDPRESS_URL || "https://cms.pacificnorthnorth.com";

export interface WordPressPage {
  id: number;
  date: string;
  date_gmt: string;
  guid: {
    rendered: string;
  };
  modified: string;
  modified_gmt: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
    protected: boolean;
  };
  excerpt: {
    rendered: string;
    protected: boolean;
  };
  author: number;
  featured_media: number;
  parent: number;
  menu_order: number;
  comment_status: string;
  ping_status: string;
  template: string;
  meta: Record<string, unknown>;
  acf?: {
    seo_title?: string;
    seo_description?: string;
    hero_image?: number | string;
    vendor_id?: number;
    listing_id?: number;
    [key: string]: unknown;
  };
  _embedded?: {
    "wp:featuredmedia"?: Array<{
      source_url: string;
      alt_text: string;
      media_details: {
        sizes: Record<string, {
          source_url: string;
          width: number;
          height: number;
        }>;
      };
    }>;
    author?: Array<{
      id: number;
      name: string;
      slug: string;
    }>;
  };
}

export interface WordPressPost {
  id: number;
  date: string;
  date_gmt: string;
  guid: {
    rendered: string;
  };
  modified: string;
  modified_gmt: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
    protected: boolean;
  };
  excerpt: {
    rendered: string;
    protected: boolean;
  };
  author: number;
  featured_media: number;
  comment_status: string;
  ping_status: string;
  sticky: boolean;
  template: string;
  format: string;
  meta: Record<string, unknown>;
  categories: number[];
  tags: number[];
  acf?: {
    seo_title?: string;
    seo_description?: string;
    hero_image?: number | string;
    vendor_id?: number;
    listing_id?: number;
    [key: string]: unknown;
  };
  _embedded?: {
    "wp:featuredmedia"?: Array<{
      source_url: string;
      alt_text: string;
      media_details: {
        sizes: Record<string, {
          source_url: string;
          width: number;
          height: number;
        }>;
      };
    }>;
    author?: Array<{
      id: number;
      name: string;
      slug: string;
    }>;
    "wp:term"?: Array<Array<{
      id: number;
      name: string;
      slug: string;
      taxonomy: string;
    }>>;
  };
}

export interface GetPostsParams {
  page?: number;
  per_page?: number;
  search?: string;
  categories?: number[];
  tags?: number[];
  author?: number;
  orderby?: "date" | "title" | "modified" | "id" | "slug";
  order?: "asc" | "desc";
  status?: "publish" | "draft" | "pending" | "private";
  _embed?: boolean;
}

export interface WordPressPostsResponse {
  posts: WordPressPost[];
  total: number;
  totalPages: number;
}

/**
 * Fetches a WordPress page by slug
 * Works on both client and server
 */
export async function getPage(slug: string): Promise<WordPressPage | null> {
  try {
    const url = `${WORDPRESS_URL}/wp-json/wp/v2/pages?slug=${encodeURIComponent(slug)}&_embed=1`;
    
    // Only use next.revalidate on server-side (when running in Node.js)
    const isServer = typeof window === 'undefined';
    const fetchOptions: RequestInit = isServer 
      ? { next: { revalidate: 300 } } // Cache for 5 minutes on server
      : { cache: 'default' }; // Use browser cache on client to allow bfcache (was 'no-store')
    
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch page: ${response.status} ${response.statusText}`);
    }

    const pages: WordPressPage[] = await response.json();
    return pages.length > 0 ? pages[0] : null;
  } catch (error) {
    // Provide more detailed error information for debugging
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorDetails = {
      slug,
      wordpressUrl: WORDPRESS_URL,
      error: errorMessage,
      isClient: typeof window !== 'undefined',
    };
    console.error(`Error fetching WordPress page "${slug}":`, errorDetails);
    
    // If it's a network error (CORS, CSP, or connection issue), log additional context
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      console.warn('Network error detected. This could be due to:', {
        csp: 'Check Content-Security-Policy connect-src directive',
        cors: 'Check WordPress CORS headers',
        url: `Verify ${WORDPRESS_URL} is accessible`,
      });
    }
    
    return null;
  }
}

/**
 * Fetches WordPress posts with optional filtering
 */
export async function getPosts(params: GetPostsParams = {}): Promise<WordPressPostsResponse> {
  try {
    const {
      page = 1,
      per_page = 10,
      search,
      categories,
      tags,
      author,
      orderby = "date",
      order = "desc",
      status = "publish",
      _embed = true,
    } = params;

    const searchParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      orderby,
      order,
      status,
    });

    if (search) {
      searchParams.append("search", search);
    }
    if (categories && categories.length > 0) {
      searchParams.append("categories", categories.join(","));
    }
    if (tags && tags.length > 0) {
      searchParams.append("tags", tags.join(","));
    }
    if (author) {
      searchParams.append("author", author.toString());
    }
    if (_embed) {
      searchParams.append("_embed", "1");
    }

    const url = `${WORDPRESS_URL}/wp-json/wp/v2/posts?${searchParams.toString()}`;
    
    // Only use next.revalidate on server-side (when running in Node.js)
    const isServer = typeof window === 'undefined';
    const fetchOptions: RequestInit = isServer 
      ? { next: { revalidate: 300 } } // Cache for 5 minutes on server
      : { cache: 'default' }; // Use browser cache on client to allow bfcache (was 'no-store')
    
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`Failed to fetch posts: ${response.statusText}`);
    }

    const posts: WordPressPost[] = await response.json();
    const total = parseInt(response.headers.get("x-wp-total") || "0", 10);
    const totalPages = parseInt(response.headers.get("x-wp-totalpages") || "0", 10);

    return {
      posts,
      total,
      totalPages,
    };
  } catch (error) {
    console.error("Error fetching WordPress posts:", error);
    return {
      posts: [],
      total: 0,
      totalPages: 0,
    };
  }
}

/**
 * Fetches a single WordPress post by slug
 */
export async function getPostBySlug(slug: string): Promise<WordPressPost | null> {
  try {
    const url = `${WORDPRESS_URL}/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}&_embed=1`;
    
    // Only use next.revalidate on server-side (when running in Node.js)
    const isServer = typeof window === 'undefined';
    const fetchOptions: RequestInit = isServer 
      ? { next: { revalidate: 300 } } // Cache for 5 minutes on server
      : { cache: 'default' }; // Use browser cache on client to allow bfcache (was 'no-store')
    
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch post: ${response.statusText}`);
    }

    const posts: WordPressPost[] = await response.json();
    return posts.length > 0 ? posts[0] : null;
  } catch (error) {
    console.error(`Error fetching WordPress post "${slug}":`, error);
    return null;
  }
}

/**
 * Fetches posts associated with a specific vendor ID
 * Uses ACF custom field `vendor_id` to filter posts
 */
export async function getVendorPosts(
  vendorId: number,
  params: Omit<GetPostsParams, "author"> = {}
): Promise<WordPressPostsResponse> {
  try {
    // WordPress REST API doesn't natively support filtering by ACF fields
    // We'll need to fetch all posts and filter client-side, or use a custom endpoint
    // For now, we'll fetch posts and filter by ACF field if available
    // In production, you may want to create a custom WordPress REST API endpoint
    
    const allPosts = await getPosts({
      ...params,
      per_page: 100, // Fetch more to increase chance of finding vendor posts
    });

    // Filter posts by vendor_id in ACF field
    const vendorPosts = allPosts.posts.filter((post) => {
      const vendorIdField = post.acf?.vendor_id;
      if (vendorIdField === undefined) return false;
      // Compare as numbers (vendorIdField is number | undefined, vendorId is number)
      return Number(vendorIdField) === Number(vendorId);
    });

    return {
      posts: vendorPosts,
      total: vendorPosts.length,
      totalPages: Math.ceil(vendorPosts.length / (params.per_page || 10)),
    };
  } catch (error) {
    console.error(`Error fetching vendor posts for vendor ${vendorId}:`, error);
    return {
      posts: [],
      total: 0,
      totalPages: 0,
    };
  }
}

/**
 * Fetches posts associated with a specific listing ID
 * Uses ACF custom field `listing_id` to filter posts
 */
export async function getListingPosts(
  listingId: number,
  params: Omit<GetPostsParams, "author"> = {}
): Promise<WordPressPostsResponse> {
  try {
    // Similar to getVendorPosts, we filter by ACF field
    const allPosts = await getPosts({
      ...params,
      per_page: 100, // Fetch more to increase chance of finding listing posts
    });

    // Filter posts by listing_id in ACF field
    const listingPosts = allPosts.posts.filter((post) => {
      const listingIdField = post.acf?.listing_id;
      if (listingIdField === undefined) return false;
      // Compare as numbers (listingIdField is number | undefined, listingId is number)
      return Number(listingIdField) === Number(listingId);
    });

    return {
      posts: listingPosts,
      total: listingPosts.length,
      totalPages: Math.ceil(listingPosts.length / (params.per_page || 10)),
    };
  } catch (error) {
    console.error(`Error fetching listing posts for listing ${listingId}:`, error);
    return {
      posts: [],
      total: 0,
      totalPages: 0,
    };
  }
}

/**
 * Gets the featured image URL from a WordPress post or page
 */
export function getFeaturedImageUrl(postOrPage: WordPressPost | WordPressPage): string | null {
  if (postOrPage._embedded?.["wp:featuredmedia"]?.[0]?.source_url) {
    return postOrPage._embedded["wp:featuredmedia"][0].source_url;
  }
  return null;
}

/**
 * Gets the featured image alt text from a WordPress post or page
 */
export function getFeaturedImageAlt(postOrPage: WordPressPost | WordPressPage): string {
  return postOrPage._embedded?.["wp:featuredmedia"]?.[0]?.alt_text || "";
}

/**
 * Gets SEO title from ACF field or falls back to post/page title
 */
export function getSEOTitle(postOrPage: WordPressPost | WordPressPage): string {
  return postOrPage.acf?.seo_title || postOrPage.title.rendered;
}

/**
 * Gets SEO description from ACF field or falls back to excerpt
 */
export function getSEODescription(postOrPage: WordPressPost | WordPressPage): string {
  if (postOrPage.acf?.seo_description) {
    return postOrPage.acf.seo_description;
  }
  // Strip HTML tags from excerpt
  const excerpt = postOrPage.excerpt.rendered || "";
  return excerpt.replace(/<[^>]*>/g, "").trim();
}

